export type Language = 'en' | 'ru' | 'pl';

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // App header
    'app.title': '🇵🇱 Polish Vocabulary',
    'app.subtitle': 'Choose categories → generate sentences → translate',
    'nav.koniugacja': 'Koniugacja Cards →',
    'nav.settings': '⚙️ Settings',
    'nav.chat': '💬 Chat',
    'btn.logout': 'Log out',
    'btn.logoutConfirm': 'Are you sure you want to log out?',

    // Categories section
    'section.conjugations': 'Conjugations',
    'section.categories': 'Categories',
    'btn.show': '▶ Show',
    'btn.hide': '▼ Hide',
    'switch.verbs': 'Verbs (koniugacja)',
    'btn.selectAll': 'Select all',
    'btn.deselectAll': 'Deselect all',
    'count.verbs': 'verbs',
    'count.words': 'words',
    'category.editWords': 'Customize words',
    'btn.done': 'Done',

    // Generate section
    'btn.generate': '✨ Generate sentences',
    'loading.generating': 'Generating sentences…',
    'error.generation': 'Generation error: ',
    'error.translation': 'Translation error: ',
    'error.loadDict': 'Failed to load dictionary.',

    // Results section
    'section.sentences': 'Sentences',
    'display.original': 'Original',
    'display.both': 'Both',
    'display.translation': 'Translation',
    'btn.translate': '🔄 Translate to Russian',
    'btn.prev': '← Prev',
    'btn.next': 'Next →',
    'btn.delete': 'Delete',
    'loading.translating': 'Translating…',
    'btn.copy': 'Copy',

    // Generate settings
    'settings.sentenceCount': 'Sentences:',
    'settings.wordsPerSentence': 'Words:',
    'settings.tense': 'Tense:',
    'settings.topic': 'Topic:',
    'settings.case': 'Case:',
    'settings.present': 'present',
    'settings.past': 'past',
    'settings.future': 'future',
    'topic.label': 'Topic',
    'topic.random': 'Random topic',
    'topic.createNew': 'Create new',
    'topic.useSaved': 'Use saved',
    'topic.remember': 'Remember',
    'topic.delete': 'Delete topic',
    'topic.deleteConfirm': 'Delete this topic from saved topics?',
    'case.random': 'Random case',

    // Login page
    'login.title': '🇵🇱 Polish Vocabulary',
    'login.password': 'Password',
    'login.submit': 'Log in',
    'login.error': 'Wrong password',

    // Settings page
    'settingsPage.title': '⚙️ Settings',
    'settingsPage.learning': '🎯 Learning',
    'settingsPage.learningLanguage': 'Learning language',
    'settingsPage.back': '← Back',
    'settingsPage.generation': '🔤 Text Generation',
    'settingsPage.translation': '🌐 Translation',
    'settingsPage.display': '🖥️ Display',
    'settingsPage.save': '💾 Save Settings',
    'settingsPage.saving': 'Saving…',
    'settingsPage.saved': '✓ Saved!',
    'settingsPage.reset': '🔄 Reset All Settings',
    'settingsPage.resetConfirm':
      'Are you sure you want to reset all settings to defaults?',

    // Chat page
    'chat.title': '💬 Chat with AI',
    'chat.send': 'Send',
    'chat.placeholder': 'Type your message…',

    // Theme
    'theme.light': '☀️',
    'theme.dark': '🌙',
    'theme.auto': 'Auto',

    // Language
    'language.label': 'Language',
    'learningLanguage.polish': 'Polish',
    'learningLanguage.lithuanian': 'Lithuanian',

    // Word expand
    'word.generateForWord': 'Generate sentences with this word',
    'word.generateForVerb': 'Generate sentences with this verb',

    // Koniugacja page
    'kon.deckTitle.1': 'KONIUGACJA I (‑ę, ‑esz)',
    'kon.deckTitle.2': 'KONIUGACJA II (‑ę, ‑isz/‑ysz)',
    'kon.deckTitle.3': 'KONIUGACJA III (‑am/‑asz)',
    'kon.deckTitle.all': 'KONIUGACJA I + II + III',
    'kon.subtitle': 'Swipe left/right or use buttons; arrow keys ← → also work.',
    'kon.prevCard': '← Back',
    'kon.nextCard': 'Next →',
    'kon.hint': 'Tip: swipe horizontally on your phone.',
    'kon.backToMain': '← Back to main',
    'kon.conjugation': 'conjugation:',
    'kon.word': 'word:',
    'kon.hideTables': 'hide tables',
    'kon.hideTranslation': 'hide translation',
    'kon.randomCard': 'random card',
    'kon.showK': 'show group',
    'kon.optPolish': 'polish',
    'kon.optRussian': 'russian',
    'kon.singular': 'singular',
    'kon.plural': 'plural',

    // Verb columns
    'verb.deselectAll': '✗ deselect',
    'verb.selectAll': '✓ all',

    // Batch filter labels
    'filter.all': 'All',
    'filter.main': 'Words',
    'filter.K': 'Conjugation',
    'filter.V': 'Single verb',
    'filter.W': 'Single word',
    'filter.category': 'Category',
    'filter.allCategories': 'All categories',

    // WordPickerModal actions
    'modal.generate': 'Generate from this category',
    'modal.filterSentences': 'Show sentences for this category',
  },

  ru: {
    // App header
    'app.title': '🇵🇱 Польская лексика',
    'app.subtitle': 'Выбери категории → сгенерируй предложения → переведи',
    'nav.koniugacja': 'Koniugacja Cards →',
    'nav.settings': '⚙️ Настройки',
    'nav.chat': '💬 Чат',
    'btn.logout': 'Выйти',
    'btn.logoutConfirm': 'Вы уверены, что хотите выйти?',

    // Categories section
    'section.conjugations': 'Конъюгации',
    'section.categories': 'Категории',
    'btn.show': '▶ Показать',
    'btn.hide': '▼ Скрыть',
    'switch.verbs': 'Глаголы (koniugacja)',
    'btn.selectAll': 'Выбрать все',
    'btn.deselectAll': 'Снять все',
    'count.verbs': 'глаголов',
    'count.words': 'слов',
    'category.editWords': 'Настроить слова',
    'btn.done': 'Готово',

    // Generate section
    'btn.generate': '✨ Сгенерировать предложения',
    'loading.generating': 'Генерируем предложения…',
    'error.generation': 'Ошибка генерации: ',
    'error.translation': 'Ошибка перевода: ',
    'error.loadDict': 'Не удалось загрузить словарь.',

    // Results section
    'section.sentences': 'Предложения',
    'display.original': 'Оригинал',
    'display.both': 'Оба',
    'display.translation': 'Перевод',
    'btn.translate': '🔄 Перевести на русский',
    'btn.prev': '← Пред',
    'btn.next': 'След →',
    'btn.delete': 'Удалить',
    'loading.translating': 'Переводим…',
    'btn.copy': 'Копировать',

    // Generate settings
    'settings.sentenceCount': 'Предложений:',
    'settings.wordsPerSentence': 'Слов:',
    'settings.tense': 'Время:',
    'settings.topic': 'Тема:',
    'settings.case': 'Падеж:',
    'settings.present': 'наст.',
    'settings.past': 'прош.',
    'settings.future': 'буд.',
    'topic.label': 'Тема',
    'topic.random': 'Случайная тема',
    'topic.createNew': 'Создать новую',
    'topic.useSaved': 'Выбрать сохранённую',
    'topic.remember': 'Запомнить',
    'topic.delete': 'Удалить тему',
    'topic.deleteConfirm': 'Удалить эту тему из сохранённых тем?',
    'case.random': 'Случайный падеж',

    // Login page
    'login.title': '🇵🇱 Польская лексика',
    'login.password': 'Пароль',
    'login.submit': 'Войти',
    'login.error': 'Неверный пароль',

    // Settings page
    'settingsPage.title': '⚙️ Настройки',
    'settingsPage.learning': '🎯 Язык обучения',
    'settingsPage.learningLanguage': 'Изучаемый язык',
    'settingsPage.back': '← Назад',
    'settingsPage.generation': '🔤 Генерация текста',
    'settingsPage.translation': '🌐 Перевод',
    'settingsPage.display': '🖥️ Отображение',
    'settingsPage.save': '💾 Сохранить настройки',
    'settingsPage.saving': 'Сохранение…',
    'settingsPage.saved': '✓ Сохранено!',
    'settingsPage.reset': '🔄 Сбросить все настройки',
    'settingsPage.resetConfirm':
      'Вы уверены, что хотите сбросить все настройки?',

    // Chat page
    'chat.title': '💬 Чат с AI',
    'chat.send': 'Отправить',
    'chat.placeholder': 'Введите сообщение…',

    // Theme
    'theme.light': '☀️',
    'theme.dark': '🌙',
    'theme.auto': 'Auto',

    // Language
    'language.label': 'Язык',
    'learningLanguage.polish': 'Польский',
    'learningLanguage.lithuanian': 'Литовский',

    // Word expand
    'word.generateForWord': 'Сгенерировать предложения с этим словом',
    'word.generateForVerb': 'Сгенерировать предложения с этим глаголом',

    // Koniugacja page
    'kon.deckTitle.1': 'KONIUGACJA I (‑ę, ‑esz)',
    'kon.deckTitle.2': 'KONIUGACJA II (‑ę, ‑isz/‑ysz)',
    'kon.deckTitle.3': 'KONIUGACJA III (‑am/‑asz)',
    'kon.deckTitle.all': 'KONIUGACJA I + II + III',
    'kon.subtitle': 'Свайп влево/вправо или кнопки; стрелки ← → тоже работают.',
    'kon.prevCard': '← Назад',
    'kon.nextCard': 'Дальше →',
    'kon.hint': 'Совет: на телефоне веди пальцем горизонтально.',
    'kon.backToMain': '← На главную',
    'kon.conjugation': 'спряжение:',
    'kon.word': 'слово:',
    'kon.hideTables': 'скрыть таблицы',
    'kon.hideTranslation': 'скрыть перевод',
    'kon.randomCard': 'случайная',
    'kon.showK': 'показать группу',
    'kon.optPolish': 'польский',
    'kon.optRussian': 'русский',
    'kon.singular': 'ед. число',
    'kon.plural': 'мн. число',

    // Verb columns
    'verb.deselectAll': '✗ снять',
    'verb.selectAll': '✓ все',

    // Batch filter labels
    'filter.all': 'Все',
    'filter.main': 'Слова',
    'filter.K': 'Конъюгация',
    'filter.V': 'Один глагол',
    'filter.W': 'Одно слово',
    'filter.category': 'Категория',
    'filter.allCategories': 'Все категории',

    // WordPickerModal actions
    'modal.generate': 'Сгенерировать из этой категории',
    'modal.filterSentences': 'Показать предложения для этой категории',
  },

  pl: {
    // App header
    'app.title': '🇵🇱 Polskie słownictwo',
    'app.subtitle': 'Wybierz kategorie → wygeneruj zdania → przetłumacz',
    'nav.koniugacja': 'Koniugacja Cards →',
    'nav.settings': '⚙️ Ustawienia',
    'nav.chat': '💬 Czat',
    'btn.logout': 'Wyloguj',
    'btn.logoutConfirm': 'Czy na pewno chcesz się wylogować?',

    // Categories section
    'section.conjugations': 'Koniugacje',
    'section.categories': 'Kategorie',
    'btn.show': '▶ Pokaż',
    'btn.hide': '▼ Ukryj',
    'switch.verbs': 'Czasowniki (koniugacja)',
    'btn.selectAll': 'Zaznacz wszystkie',
    'btn.deselectAll': 'Odznacz wszystkie',
    'count.verbs': 'czasowników',
    'count.words': 'słów',
    'category.editWords': 'Dostosuj słowa',
    'btn.done': 'Gotowe',

    // Generate section
    'btn.generate': '✨ Wygeneruj zdania',
    'loading.generating': 'Generujemy zdania…',
    'error.generation': 'Błąd generowania: ',
    'error.translation': 'Błąd tłumaczenia: ',
    'error.loadDict': 'Nie udało się załadować słownika.',

    // Results section
    'section.sentences': 'Zdania',
    'display.original': 'Oryginał',
    'display.both': 'Oba',
    'display.translation': 'Tłumaczenie',
    'btn.translate': '🔄 Przetłumacz na rosyjski',
    'btn.prev': '← Poprz',
    'btn.next': 'Nast →',
    'btn.delete': 'Usuń',
    'loading.translating': 'Tłumaczymy…',
    'btn.copy': 'Kopiuj',

    // Generate settings
    'settings.sentenceCount': 'Zdań:',
    'settings.wordsPerSentence': 'Słów:',
    'settings.tense': 'Czas:',
    'settings.topic': 'Temat:',
    'settings.case': 'Przypadek:',
    'settings.present': 'teraźn.',
    'settings.past': 'przeszły',
    'settings.future': 'przyszły',
    'topic.label': 'Temat',
    'topic.random': 'Losowy temat',
    'topic.createNew': 'Utwórz nowy',
    'topic.useSaved': 'Użyj zapisanych',
    'topic.remember': 'Zapamiętaj',
    'topic.delete': 'Usuń temat',
    'topic.deleteConfirm': 'Usunąć ten temat z zapisanych tematów?',
    'case.random': 'Losowy przypadek',

    // Login page
    'login.title': '🇵🇱 Polskie słownictwo',
    'login.password': 'Hasło',
    'login.submit': 'Zaloguj',
    'login.error': 'Nieprawidłowe hasło',

    // Settings page
    'settingsPage.title': '⚙️ Ustawienia',
    'settingsPage.learning': '🎯 Język nauki',
    'settingsPage.learningLanguage': 'Język nauki',
    'settingsPage.back': '← Wstecz',
    'settingsPage.generation': '🔤 Generowanie tekstu',
    'settingsPage.translation': '🌐 Tłumaczenie',
    'settingsPage.display': '🖥️ Wyświetlanie',
    'settingsPage.save': '💾 Zapisz ustawienia',
    'settingsPage.saving': 'Zapisywanie…',
    'settingsPage.saved': '✓ Zapisano!',
    'settingsPage.reset': '🔄 Resetuj wszystkie ustawienia',
    'settingsPage.resetConfirm':
      'Czy na pewno chcesz zresetować wszystkie ustawienia do domyślnych?',

    // Chat page
    'chat.title': '💬 Czat z AI',
    'chat.send': 'Wyślij',
    'chat.placeholder': 'Wpisz wiadomość…',

    // Theme
    'theme.light': '☀️',
    'theme.dark': '🌙',
    'theme.auto': 'Auto',

    // Language
    'language.label': 'Język',
    'learningLanguage.polish': 'Polski',
    'learningLanguage.lithuanian': 'Litewski',

    // Word expand
    'word.generateForWord': 'Wygeneruj zdania z tym słowem',
    'word.generateForVerb': 'Wygeneruj zdania z tym czasownikiem',

    // Koniugacja page
    'kon.deckTitle.1': 'KONIUGACJA I (‑ę, ‑esz)',
    'kon.deckTitle.2': 'KONIUGACJA II (‑ę, ‑isz/‑ysz)',
    'kon.deckTitle.3': 'KONIUGACJA III (‑am/‑asz)',
    'kon.deckTitle.all': 'KONIUGACJA I + II + III',
    'kon.subtitle': 'Przesuń w lewo/prawo lub użyj przycisków; strzałki ← → też działają.',
    'kon.prevCard': '← Wstecz',
    'kon.nextCard': 'Dalej →',
    'kon.hint': 'Wskazówka: przesuń palcem na telefonie.',
    'kon.backToMain': '← Strona główna',
    'kon.conjugation': 'koniugacja:',
    'kon.word': 'słowo:',
    'kon.hideTables': 'ukryj tabele',
    'kon.hideTranslation': 'ukryj tłumaczenie',
    'kon.randomCard': 'losowa',
    'kon.showK': 'pokaż grupę',
    'kon.optPolish': 'polski',
    'kon.optRussian': 'rosyjski',
    'kon.singular': 'liczba pojedyncza',
    'kon.plural': 'liczba mnoga',

    // Verb columns
    'verb.deselectAll': '✗ odznacz',
    'verb.selectAll': '✓ wszystkie',

    // Batch filter labels
    'filter.all': 'Wszystkie',
    'filter.main': 'Słowa',
    'filter.K': 'Koniugacja',
    'filter.V': 'Jeden czasownik',
    'filter.W': 'Jedno słowo',
    'filter.category': 'Kategoria',
    'filter.allCategories': 'Wszystkie kategorie',

    // WordPickerModal actions
    'modal.generate': 'Generuj z tej kategorii',
    'modal.filterSentences': 'Pokaż zdania dla tej kategorii',
  },
};

export function t(lang: Language, key: string): string {
  return translations[lang]?.[key] ?? translations['en']?.[key] ?? key;
}
